import React, { useContext, ReactNode, useState } from 'react'
import { styled, alpha, useTheme } from '@mui/material/styles'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import { TextField, FilterOptionsState, Button } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'

import { AppContext } from '../contexts/AppProvider'
import { MeasureDataContext } from '../contexts/MeasureDataProvider'
import useIsMobile from '../hooks/useIsMobile'
import { Measure } from '../types'
import { upperFirst } from '../utils/miscUtils'
import NewMeasureRequestDialog from './NewMeasureRequestDialog'
import MeasureSearchMobileOverlay from './MeasureSearchMobileOverlay'
import TabMeasureIcon from './TabMeasureIcon'
import { createAutocompleteRenderOption } from './helpers/createAutocompleteRenderOption'
import { useAppNavigate } from '../hooks/useAppNavigate'


const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.25),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.30)
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}))

interface TempMeasureOptionType {
  displayName: string
}
const filter = createFilterOptions<TempMeasureOptionType>()


const HeaderMeasureSearchBar: React.FC = () => {
  const { navigateToConverter } = useAppNavigate()
  const theme = useTheme()

  const { allMeasures } = useContext(MeasureDataContext)
  const { appState } = useContext(AppContext)
  const [searchedMeasure, setSearchedMeasure] = useState<Measure | null>(null)
  const [openNewMeasureRequestDialog, toggleOpenNewMeasureRequestDialog] = useState(false)
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState<boolean>(false)
  const isMobile = useIsMobile()


  const autocompleteRenderOption = createAutocompleteRenderOption(
    (option: any) => <TabMeasureIcon measure={option} />,
    (text: string, index: number) => (index === 0 ? upperFirst(text) : text)
  )


  const autocompleteRenderInput = (params: any): ReactNode => {
    return (
      <TextField
        {...params}
        placeholder="Search converters..."
        InputProps={{
          ...params.InputProps,
          sx: {
            '& .MuiOutlinedInput-notchedOutline': {
              // borderColor: theme.palette.custom.cobaltBlue, // Border color of the input
              border: 'none'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              // borderColor: theme.palette.custom.cobaltBlue, // Border color when hovered
              border: 'none'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              // borderColor: theme.palette.custom.cobaltBlue, // Border color when focused
              border: 'none'
            },
            '&.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
              marginRight: '4px!important' // needed to position properly icon made with font-awesome
            },
            '& input::selection': {
              backgroundColor: theme.palette.custom.cobaltBlue, // Your desired highlight color
              color: 'white' // Text color when highlighted
            }
          }
        }}
      />
    )
  }


  const handleOptionSelect = (newCurrentMeasure: Measure): void => {
    if (newCurrentMeasure === undefined || newCurrentMeasure === null) {
      return
    }

    // Clear the input field using this state
    setSearchedMeasure(null)

    navigateToConverter(
      newCurrentMeasure,
      appState.tabs[newCurrentMeasure.codeName].fromUnit,
      appState.tabs[newCurrentMeasure.codeName].toUnit
    )
  }

  const searchAutocompleteFilterOptions = (options: any[], params: FilterOptionsState<TempMeasureOptionType>): TempMeasureOptionType[] => {
    const filtered = filter(options, params)

    const inputValue = params.inputValue.toLowerCase()
    const isSubstring = Object.values(allMeasures).some(option =>
      option.displayName.toLowerCase().includes(inputValue)
    )

    if (params.inputValue !== '' && !isSubstring) {
      filtered.push({
        displayName: `Request measure "${params.inputValue}"`
      })
    }

    return filtered
  }

  return (
    <>
      <Search sx={{ display: { xs: 'none', sm: 'block' } }}>
        <div className='measures-search-bar'>
          <Autocomplete
            id="multiple-limit-tags"
            blurOnSelect
            selectOnFocus
            autoHighlight
            clearOnEscape
            openOnFocus
            options={Object.values(allMeasures).map((measure) => measure)}
            getOptionLabel={(option) => upperFirst(option.displayName) }
            size="small"
            value={searchedMeasure}
            onChange={(event, newMeasure) => {
              if (newMeasure?.displayName.startsWith('Request ') === true) {
                toggleOpenNewMeasureRequestDialog(true)
              } else if (newMeasure?.displayName !== null) {
                handleOptionSelect(newMeasure)
              }
            }}
            renderInput={autocompleteRenderInput}
            renderOption={autocompleteRenderOption}
            filterOptions={searchAutocompleteFilterOptions}
            sx={{ minWidth: 250 }}
            popupIcon={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '0.7em', color: theme.palette.custom.cobaltBlue }} />}
          />
        </div>
      </Search>
      {isMobile &&
        <Button
          variant="text"
          size='large'
          sx={{
            width: '40px',
            height: '40px',
            borderRadius: '4px',
            padding: 0,
            minWidth: 0 // Prevent MUI from overriding the width
          }}
          onClick={() => setIsMobileSearchOpen(true)}
          aria-label="Search converters" // Clear description of what the button does
          aria-haspopup="dialog" // Indicates this opens a dialog/overlay
          aria-expanded={isMobileSearchOpen} // Dynamic state of the overlay
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: '8px', fontSize: '1.2em', color: 'white' }} />
        </Button>
      }
      <NewMeasureRequestDialog open={openNewMeasureRequestDialog} setOpen={toggleOpenNewMeasureRequestDialog} />
      <MeasureSearchMobileOverlay
        open={isMobileSearchOpen}
        setOpen={setIsMobileSearchOpen}
        onOpenNewMeasureRequestDialog={() => toggleOpenNewMeasureRequestDialog(true)}
      />
    </>
  )
}

export default HeaderMeasureSearchBar
